<template>
    <div class="continue-outer-container" :class="{ 'dark-mode': darkMode }">
        <Toast />
        <!-- Loading overlay for dark mode -->
        <div v-if="darkMode && isLoadingEmailAuth" class="dark-loading-overlay">
            <div class="loading-spinner"></div>
            <p class="loading-text">{{ t('general.loading') }}...</p>
        </div>

        <div v-if="showEmailVerification" class="verification-modal-container">
            <EmailVerificationFlow
                mode="modal"
                :dark-mode="darkMode"
                @verified="handleVerificationSuccess"
            />
        </div>

        <div v-else class="main-auth-content">
            <!-- Logo -->
            <h1 class="welcome-title">
                {{
                    displayName
                        ? `${$t('login.welcome')} ${displayName}`
                        : $t('login.welcome')
                }}
            </h1>
            <p class="continue-description">
                {{ $t('login.welcome-description-1') }}
                <strong>{{ $t('login.welcome-description-2') }}</strong>
                {{ $t('login.welcome-description-3') }}
                <strong
                    ><router-link
                        to="/legal/terms-of-use"
                        target="_blank"
                        class="link_terms"
                        >{{ $t('login.welcome-description-4') }}</router-link
                    ></strong
                >
                {{ $t('login.welcome-description-5') }}
                <strong
                    ><router-link
                        to="/legal/privacy-policy"
                        target="_blank"
                        class="link_terms"
                        >{{ $t('login.welcome-description-6') }}</router-link
                    ></strong
                >
            </p>

            <!-- Continue with Google -->
            <Button
                class="shadow-btn google-button"
                :label="`${$t('login.continue-with')} Google`"
                @click="signInWithGoogle"
                :disabled="loadingOAuth"
            >
                <!-- Google icon png -->
                <template #icon>
                    <img
                        src="@/assets/images/continue/Google.svg"
                        alt="Google"
                        class="button-icon"
                    />
                </template>
            </Button>

            <!-- Continue with X -->
            <Button
                class="shadow-btn x-button"
                :label="`${$t('login.continue-with')} X`"
                @click="signInWithX"
                :disabled="loadingOAuth"
            >
                <!-- X icon png -->
                <template #icon>
                    <img
                        src="@/assets/images/continue/x.svg"
                        alt="X"
                        class="button-icon"
                    />
                </template>
            </Button>

            <!-- Divider with 'or' text -->
            <div class="divider">
                <div class="divider-line"></div>
                <span class="divider-text">{{ $t('general.or') }}</span>
                <div class="divider-line"></div>
            </div>

            <!-- Email input field -->
            <div class="email-input-container">
                <div class="input-wrapper">
                    <InputText
                        ref="emailInputRef"
                        placeholder="email@example.com"
                        class="input-field"
                        v-model="email"
                        @keyup.enter="continueWithEmail"
                        :class="{ 'checking-email': isCheckingEmail }"
                    />
                    <div v-if="isCheckingEmail" class="input-spinner">
                        <i class="pi pi-spin pi-spinner"></i>
                    </div>
                </div>
            </div>

            <!-- Password field (shown only after email is validated) -->
            <transition name="slide-fade">
                <div v-if="showPassword" class="email-input-container">
                    <Password
                        id="password"
                        unstyled
                        placeholder="password"
                        v-model="password"
                        :feedback="false"
                        @keyup.enter="handleEmailLogin"
                    />
                </div>
            </transition>

            <!-- Continue with email button -->
            <Button
                class="shadow-btn email-button"
                @click="showPassword ? handleEmailLogin() : continueWithEmail()"
                :loading="isLoadingEmailAuth"
                :disabled="isLoadingEmailAuth"
            >
                <img
                    src="@/assets/images/continue/icon-candado.svg"
                    alt="Email"
                    class="button-icon email-button-icon"
                />
                <!-- Button label with transition -->
                <transition name="button-text-fade" mode="out-in">
                    <span
                        class="button-text"
                        :key="
                            isEmailRegistered === undefined
                                ? 'continue'
                                : isEmailRegistered === false
                                  ? 'signup'
                                  : 'login'
                        "
                    >
                        {{
                            isEmailRegistered === undefined
                                ? `${$t('login.continue-with')} Email`
                                : isEmailRegistered === false
                                  ? `${$t('login.signup')}`
                                  : `${$t('general.login')}`
                        }}
                    </span>
                </transition>
            </Button>

            <!-- Forgot password link (only shown when password field is visible) -->
            <transition name="slide-fade">
                <div
                    v-if="showPassword && isEmailRegistered"
                    class="forgot-password-link"
                >
                    <a
                        @click="resetPassword"
                        class="text-sm forgot-password-link-text"
                    >
                        {{ $t('login.forgot-password') }}
                    </a>
                </div>
            </transition>
        </div>
    </div>
</template>

<script setup>
// Vue core imports
import { ref, watch, onMounted, nextTick, onActivated } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'

// Store imports
import { useAuthStore } from '@/stores/auth.store'
import { useConversationStore } from '@/stores/conversation.store'
import { useModalStore } from '@/stores/modal.store'
import { usePaymentStore } from '@/stores/payment.store'

// Service imports
import UserService from '@/services/api/user.service.js'
import messageService from '@/services/api/message.service'

// Utilities
import { debounce } from '@/utils'
import * as authUtils from '@/utils/auth.js'
import { linkAnonymousAccount } from '@/utils/auth.js'
import * as toastUtils from '@/utils/toast.js'

// Date/Time utilities
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'

// UI Components
import { useToast } from 'primevue/usetoast'
import Toast from 'primevue/toast'
import Button from 'primevue/button'
import InputText from 'primevue/inputtext'
import Password from 'primevue/password'
import EmailVerificationFlow from '@/components/authentication/EmailVerificationFlow.vue'

// Import firebase
import firebase from 'firebase/compat/app'

// Setup
dayjs.extend(utc)
dayjs.extend(timezone)

const router = useRouter()
const toast = useToast()
const { t } = useI18n()
const authStore = useAuthStore()
const conversationStore = useConversationStore()
const modalStore = useModalStore()
const paymentStore = usePaymentStore()

const props = defineProps({
    isPage: {
        type: Boolean,
        default: false,
    },
    hideTitle: {
        type: Boolean,
        default: false,
    },
    darkMode: {
        type: Boolean,
        default: false,
    },
    displayName: {
        type: String,
        default: '',
    },
    disableRedirect: {
        type: Boolean,
        default: false,
    },
})

// Form state
const email = ref('')
const password = ref('')
const showPassword = ref(false)
const isEmailRegistered = ref(undefined)
const emailPreviouslyChecked = ref(false)
const isCheckingEmail = ref(false)
const loadingOAuth = ref(false)
const isLoadingEmailAuth = ref(false)
const showEmailVerification = ref(false)

// Refs
const emailInputRef = ref(null)

// Ensure email is always lowercase
watch(
    email,
    (newVal) => {
        if (newVal && newVal !== newVal.toLowerCase()) {
            email.value = newVal.toLowerCase()
        }
    },
    { immediate: true }
)

// Create a debounced version of the email check function
const debouncedCheckEmail = debounce(async (newEmail) => {
    if (authUtils.validEmail(newEmail)) {
        isCheckingEmail.value = true
        const uniqueEmail = await authUtils.isUniqueEmail(newEmail)
        isEmailRegistered.value = !uniqueEmail
        isCheckingEmail.value = false
    }
}, 500) // 500ms delay

// Watch for changes to email after initial check
watch(email, (newEmail, oldEmail) => {
    if (
        emailPreviouslyChecked.value &&
        showPassword.value &&
        newEmail !== oldEmail
    ) {
        // Use the debounced version to limit API calls
        debouncedCheckEmail(newEmail)
    }
})

function validEmail(email) {
    return authUtils.validEmail(email)
}

async function isUniqueEmail(email) {
    return await authUtils.isUniqueEmail(email)
}

function resetAuthForm() {
    email.value = ''
    password.value = ''
    showPassword.value = false
    isEmailRegistered.value = undefined
    emailPreviouslyChecked.value = false
    isCheckingEmail.value = false
}

async function handlePendingAction({ isAnonymousUpgrade = false } = {}) {
    const pendingAction = localStorage.getItem('pendingAction')
    if (pendingAction === 'buyStars') {
        localStorage.removeItem('pendingAction')
        // We don't remove pendingPackId here, buy-stars modal uses it if needed, or we might need to pass it
        // Actually buy-stars modal logic I added sets showBuyStarsModal to true.
        // And pre-selects pack? No, the logic in buy-stars.vue just stored it.
        // We need to re-open the modal.
        modalStore.closeModal()
        paymentStore.state.showBuyStarsModal = true
        return
    }

    if (pendingAction === 'signupForChatCredits') {
        localStorage.removeItem('pendingAction')
        const conversationId = localStorage.getItem('pendingConversationId')
        localStorage.removeItem('pendingConversationId')

        // Only grant the +5 bonus when this auth flow was an anonymous->registered upgrade
        // (i.e., first-time registration/link), not when logging into an existing account.
        if (conversationId && isAnonymousUpgrade) {
            try {
                const resp =
                    await messageService.grantSignupBonusMessages(
                        conversationId
                    )
                const remaining =
                    resp?.data?.remaining ||
                    resp?.data?.data?.remaining ||
                    resp?.remaining ||
                    null

                if (
                    remaining &&
                    conversationStore.state.currentConversation?._id ===
                        conversationId
                ) {
                    // Update current conversation remaining credits immediately; socket will also update.
                    conversationStore.state.currentConversation = {
                        ...conversationStore.state.currentConversation,
                        paidMessaging: {
                            ...(conversationStore.state.currentConversation
                                .paidMessaging || {}),
                            remaining,
                        },
                    }
                }

                // Also update the conversations list entry if present (keeps list + chat consistent)
                if (
                    remaining &&
                    Array.isArray(conversationStore.state.conversations)
                ) {
                    const idx = conversationStore.state.conversations.findIndex(
                        (c) => c?._id === conversationId
                    )
                    if (idx > -1) {
                        conversationStore.state.conversations[idx] = {
                            ...conversationStore.state.conversations[idx],
                            paidMessaging: {
                                ...(conversationStore.state.conversations[idx]
                                    .paidMessaging || {}),
                                remaining,
                            },
                        }
                    }
                }
            } catch (err) {
                console.error(
                    'Failed to grant signup bonus messages for conversation:',
                    conversationId,
                    err
                )
            }
        }

        // Close the auth modal and let the user continue in chat
        modalStore.closeModal()
    }
}

async function handlePostLoginRedirect() {
    const targetCreatorId = localStorage.getItem('loginTargetCreatorId')
    const targetCreatorUsername = localStorage.getItem(
        'loginTargetCreatorUsername'
    )

    if (targetCreatorId) {
        console.log(
            `Post-login target found: ${targetCreatorId}. Attempting to open chat.`
        )
        try {
            // Pre-populate usersMap with preview data if available
            const previewCreatorDataStr =
                localStorage.getItem('previewCreatorData')
            if (previewCreatorDataStr) {
                try {
                    const creatorData = JSON.parse(previewCreatorDataStr)
                    if (!conversationStore.state.usersMap[targetCreatorId]) {
                        conversationStore.state.usersMap[targetCreatorId] =
                            creatorData
                        console.log(
                            'Pre-populated usersMap with preview creator data'
                        )
                    }
                } catch (parseError) {
                    console.warn(
                        'Failed to parse preview creator data:',
                        parseError
                    )
                }
            }

            const success =
                await conversationStore.findOrCreateConversationWithUser(
                    targetCreatorId,
                    targetCreatorUsername || null
                )
            if (success) {
                console.log(
                    'Target conversation found/created. Redirecting to chat...'
                )

                // Only clear after successful conversation creation
                localStorage.removeItem('loginTargetCreatorId')
                localStorage.removeItem('loginTargetCreatorUsername')
                localStorage.removeItem('previewCreatorData')

                await waitForAppInitialization()
                router.push({ name: 'client-chats' })
                return true // Indicate that custom redirect happened
            } else {
                console.error(
                    'Failed to find or create target conversation after login.'
                )
                // Keep localStorage for retry by getRedirectRoute or Chats.vue
                return false // Proceed with default redirect
            }
        } catch (chatError) {
            console.error(
                'Error finding/creating target conversation after login:',
                chatError
            )
            // Keep localStorage for retry by getRedirectRoute or Chats.vue
            return false // Proceed with default redirect
        }
    } else {
        return false // No target stored, proceed with default redirect
    }
}

// Auto-upgrade to client with pending displayname
async function autoUpgradeToClient(displayname) {
    try {
        console.log('Auto-upgrading to client with displayname:', displayname)

        const username = displayname
            .trim()
            .toLowerCase()
            .replace(/\s+/g, '-')
            .replace(/-+$/g, '')

        // Step 1: Update displayname and username
        const updateResponse = await UserService.updateUser({
            displayname: displayname,
            username: username,
        })

        if (updateResponse.status !== 200) {
            // If username is taken, fall back to account creation page
            if (updateResponse.data?.msg === 'This username is already taken') {
                console.warn('Username taken, falling back to account-creation')
                return false
            }
            throw new Error(updateResponse.data?.msg || 'User update failed')
        }

        // Step 2: Small delay to avoid MongoDB write conflict
        await new Promise((resolve) => setTimeout(resolve, 300))

        // Step 3: Upgrade to client (sets typeofuser and finishedSetup)
        try {
            await UserService.upgradeToClient()
        } catch (upgradeError) {
            // Check if error is because user is already a Client (from another tab)
            const errorMessage =
                upgradeError?.response?.data?.message ||
                upgradeError?.message ||
                ''
            const isAlreadyClientError =
                errorMessage.toLowerCase().includes('already') &&
                errorMessage.toLowerCase().includes('client')

            if (isAlreadyClientError) {
                console.log(
                    'User is already a Client (upgraded in another tab), fetching fresh data'
                )

                try {
                    // Fetch fresh user data to get current state
                    const freshUser = await UserService.getCurrentUser()
                    if (freshUser?.status === 200 && freshUser.data) {
                        authStore.updateUserData(freshUser.data)

                        // If user is already a Client (regardless of finishedSetup), we're good
                        if (freshUser.data.typeofuser === 'Client') {
                            console.log(
                                'User is already set up as Client, proceeding to clean up and return success'
                            )

                            // Clean up since we're already upgraded
                            localStorage.removeItem('pendingClientDisplayname')
                            localStorage.removeItem('previewCreatorData')

                            // Return true to indicate success (user is already a client)
                            return true
                        }
                    }
                } catch (fetchError) {
                    console.warn(
                        'Failed to fetch fresh user data, but user is already a client per backend error:',
                        fetchError
                    )
                }

                // If we get here, backend said "already a client" so we trust it and proceed
                // Clean up and return success even if we couldn't verify
                console.log(
                    'Trusting backend "already a client" error, proceeding as success'
                )
                localStorage.removeItem('pendingClientDisplayname')
                localStorage.removeItem('previewCreatorData')

                // Manually set the critical fields since we know user is a client
                authStore.user.data.typeofuser = 'Client'
                authStore.user.data.finishedSetup = true

                return true
            }
            // Re-throw if it's a different error
            throw upgradeError
        }

        // Step 4: Handle referral code if present
        const refCode = localStorage.getItem('loginTargetCreatorRefCode')
        if (refCode) {
            try {
                const referralApi = (
                    await import('@/services/api/referral.service')
                ).default
                await referralApi.createClientReferral(refCode)
                localStorage.removeItem('loginTargetCreatorRefCode')
            } catch (e) {
                console.warn('Referral failed:', e)
            }
        }

        // Step 5: Fetch fresh user data from backend
        const freshUser = await UserService.getCurrentUser()
        if (freshUser?.status === 200 && freshUser.data) {
            // Ensure the auth store has the correct type and finishedSetup
            authStore.updateUserData(freshUser.data)
            // Force update these critical fields to be sure
            authStore.user.data.typeofuser = 'Client'
            authStore.user.data.finishedSetup = true
            console.log(
                'Updated auth store with Client type:',
                authStore.user.data
            )
        } else {
            // Fallback if fetch fails
            authStore.user.data.typeofuser = 'Client'
            authStore.user.data.finishedSetup = true
            console.warn('Could not fetch fresh data, manually set Client type')
        }

        // Step 6: Clean up only the displayname, keep creator info for navigation
        localStorage.removeItem('pendingClientDisplayname')
        localStorage.removeItem('previewCreatorData')

        // Step 7: Create conversation with target creator (will be set as current)
        const creatorId = localStorage.getItem('loginTargetCreatorId')
        const creatorUsername = localStorage.getItem(
            'loginTargetCreatorUsername'
        )
        if (creatorId && creatorUsername) {
            try {
                console.log(
                    `Creating conversation with creator ${creatorId}...`
                )
                const conversationCreated =
                    await conversationStore.findOrCreateConversationWithUser(
                        creatorId,
                        creatorUsername
                    )
                if (conversationCreated) {
                    console.log(
                        'Conversation created/found and set as current:',
                        conversationStore.state.currentConversation?._id
                    )

                    // Give extra time for DB writes and state updates to complete
                    await new Promise((resolve) => setTimeout(resolve, 300))
                } else {
                    console.error(
                        'findOrCreateConversationWithUser returned false'
                    )
                }
            } catch (e) {
                console.error('Failed to create conversation with creator:', e)
            }
        }

        // Note: We keep loginTargetCreatorId and loginTargetCreatorUsername
        // They will be cleaned up by handlePostLoginRedirect or after navigation

        console.log('Auto-upgrade completed successfully')
        return true
    } catch (error) {
        console.error('Auto-upgrade failed:', error)
        return false
    }
}

// Centralized navigation logic function
async function getRedirectRoute(userData, isNewUser) {
    const actualUserType = userData?.typeofuser
    const finishedSetup = userData?.finishedSetup

    if (!actualUserType) {
        console.error(
            'User type not found in backend response for redirection logic!',
            userData
        )
        return null // Signal to caller to handle error (e.g., show toast, redirect to home)
    }

    // Check for pending client displayname from preview flow
    const pendingDisplayname = localStorage.getItem('pendingClientDisplayname')
    if (pendingDisplayname && (isNewUser || !finishedSetup)) {
        console.log('Pending displayname found, auto-upgrading to client')
        const upgraded = await autoUpgradeToClient(pendingDisplayname)
        console.log('autoUpgradeToClient result:', upgraded)
        if (upgraded) {
            return 'client-chats'
        }
        console.warn(
            'Auto-upgrade failed; falling back to account creation flow'
        )
    }

    // For new users OR users who haven't finished setup, check language selection first
    if (isNewUser || !finishedSetup) {
        // First check if language has been explicitly selected (using userData instead of localStorage)
        const hasLanguage = userData?.uiLanguage
        if (!hasLanguage) {
            return 'language-selection'
        }
        return 'account-creation'
    }

    // For existing users who have finished setup
    if (actualUserType === 'Creator') {
        // Since finishedSetup means KYC is good, just go to creator-chats
        return 'creator-chats'
    } else if (actualUserType === 'Client') {
        return 'client-chats'
    }

    if (actualUserType === 'Admin') {
        return 'admin-decaf-payouts'
    }

    console.error(
        'Unhandled user type or state for redirection (existing, finishedSetup):',
        userData
    )
    return null // Fallback
}

async function continueWithEmail() {
    // Validate email
    if (!validEmail(email.value)) {
        toastUtils.showWarning(toast, t('login.invalid-email'))
        return
    }

    // Check if the email is already registered
    try {
        isCheckingEmail.value = true
        const uniqueEmail = await isUniqueEmail(email.value)
        isEmailRegistered.value = !uniqueEmail

        // Show password field and focus it after a brief delay to allow animation
        showPassword.value = true
        emailPreviouslyChecked.value = true

        // Focus password field after animation completes
        setTimeout(() => {
            const passwordInput = document.querySelector('#password input')
            if (passwordInput) {
                passwordInput.focus()
            }
        }, 300) // Match this with your slide-fade transition duration

        isCheckingEmail.value = false
    } catch (error) {
        isCheckingEmail.value = false
        console.error('Error checking email:', error)
        toastUtils.showError(toast, t('general.general-error'))
    }
}

async function handleEmailLogin() {
    if (!password.value) {
        toastUtils.showWarning(toast, t('login.enter-password'))
        return
    }

    // Password length check
    if (password.value.length < 6) {
        toastUtils.showWarning(
            toast,
            t('login.password-min-length', { min: 6 })
        )
        return
    }

    if (isEmailRegistered.value) {
        // Login with existing account
        await loginWithEmail()
    } else {
        // Create new account
        await signupWithEmail()
    }
}

async function loginWithEmail() {
    try {
        isLoadingEmailAuth.value = true
        const result = await authUtils.loginWithEmail(
            email.value,
            password.value
        )

        // NOTE: Email verification for creators is temporarily disabled
        // Uncomment the block below to re-enable
        // if (result.success && result.requiresEmailVerification) {
        //     isLoadingEmailAuth.value = false
        //     showEmailVerification.value = true
        //     return
        // }

        if (result.success && result.user) {
            toastUtils.showSuccess(
                toast,
                t('general.welcome'),
                t('login.success')
            )

            // Manually update the auth store to make it available for subsequent actions
            const currentFirebaseUser = firebase.auth().currentUser
            if (currentFirebaseUser) {
                authStore.setUser(currentFirebaseUser)
            }
            authStore.user.data = result.user
            console.log(
                'Auth store updated directly after email login:',
                authStore.user.data
            )

            // Check if redirect is disabled
            if (props.disableRedirect) {
                // Don't redirect, just return - let the parent component handle what happens next
                return
            }

            const redirected = await handlePostLoginRedirect()
            if (redirected) return

            const routeName = await getRedirectRoute(result.user, false) // false for isNewUser

            if (routeName) {
                await waitForAppInitialization()
                router.push({ name: routeName })
            } else {
                // Handle error case where routeName is null (e.g., user type missing)
                toastUtils.showError(toast, t('general.general-error'))
            }
        } else {
            toastUtils.showWarning(toast, t('login.check-email-and-password'))
        }
    } catch (error) {
        console.error('Login error:', error)
        toastUtils.showWarning(toast, t('login.check-email-and-password'))
    } finally {
        isLoadingEmailAuth.value = false
    }
}

async function signupWithEmail() {
    try {
        isLoadingEmailAuth.value = true

        // Check if we are upgrading an anonymous user
        const isAnonymous = firebase.auth().currentUser?.isAnonymous

        if (isAnonymous) {
            const credential = firebase.auth.EmailAuthProvider.credential(
                email.value,
                password.value
            )
            const linkResult = await linkAnonymousAccount(credential)

            if (linkResult.success) {
                // Update backend to remove isAnonymous flag
                await UserService.updateUser({
                    isAnonymous: false,
                    email: email.value,
                })

                // Fetch fresh user data
                await authStore.updateUserData(true)

                toastUtils.showSuccess(
                    toast,
                    t('general.welcome'),
                    t('login.success')
                )

                // Handle pending actions. This flow upgrades an anonymous user (first registration).
                await handlePendingAction({ isAnonymousUpgrade: true })

                isLoadingEmailAuth.value = false
                return
            } else {
                // Handle link error (e.g. email already in use)
                let errorMessage = linkResult.error
                if (linkResult.code === 'auth/email-already-in-use') {
                    errorMessage = t('login.invalid-email') // Or a more specific message
                }
                toastUtils.showError(toast, errorMessage)
                isLoadingEmailAuth.value = false
                return
            }
        }

        isLoadingEmailAuth.value = true
        // Pass redirect URL for verification email
        const redirectUrl = window.location.origin + '/email-verification'
        const result = await authUtils.signupWithEmail(
            email.value,
            password.value,
            redirectUrl
        )

        if (result.success && result.data) {
            toastUtils.showSuccess(
                toast,
                t('login.user-registered'),
                t('login.user-registered-detail')
            )

            const currentFirebaseUser = firebase.auth().currentUser
            if (currentFirebaseUser) {
                authStore.setUser(currentFirebaseUser)
            }
            authStore.user.data = result.data
            console.log(
                'Auth store updated directly after signup:',
                authStore.user.data
            )

            isLoadingEmailAuth.value = false

            // NOTE: Email verification for creators is temporarily disabled
            // All users now proceed with normal flow (language-selection first)
            // Uncomment the block below to re-enable email verification for Creators
            // if (result.data?.typeofuser === 'Creator') {
            //     showEmailVerification.value = true
            // } else {
            // For all users, proceed with normal flow
            // Check if redirect is disabled
            if (props.disableRedirect) {
                // Handle pending actions (e.g., signup bonus) before closing
                // This is a new registration (not anonymous upgrade, but still new), so pass true
                // to be consistent with OAuth signup logic which uses result.isNew
                await handlePendingAction({ isAnonymousUpgrade: true })
                // Close the modal since we're staying on the same route
                modalStore.closeModal()
                return
            }

            const redirected = await handlePostLoginRedirect()
            if (redirected) return

            const routeName = await getRedirectRoute(result.data, true) // true for isNewUser

            if (routeName) {
                await waitForAppInitialization()
                router.push({ name: routeName })
            } else {
                // Handle error case where routeName is null (e.g., user type missing)
                toastUtils.showError(toast, t('general.general-error'))
            }
            // }
        } else {
            // Handle signup failure reported by authUtils.signupWithEmail
            console.error('Signup failed:', result.error)
            toastUtils.showError(
                toast,
                result.error || t('general.general-error'),
                null
            )
        }
    } catch (error) {
        // Catch unexpected errors during the signup process itself
        console.error('Unexpected error during signup:', error)
        toastUtils.showError(
            toast,
            t('general.general-error'),
            error.message || t('login.signup-error-detail')
        )
    } finally {
        isLoadingEmailAuth.value = false
    }
}

async function resetPassword() {
    // Clear any potential leftover redirect targets on password reset attempt
    localStorage.removeItem('loginTargetCreatorId')
    localStorage.removeItem('loginTargetCreatorUsername')
    localStorage.removeItem('previewCreatorData')

    if (!email.value) {
        toastUtils.showWarning(toast, t('login.enter-email'))
        return
    }

    const success = await authUtils.sendPasswordResetEmail(email.value)
    if (success) {
        toastUtils.showInfo(toast, t('login.check-email'))
    } else {
        toastUtils.showWarning(toast, t('general.general-error'))
    }
}

// --- Function to handle the result from signInWithProvider ---
async function handleAuthResult(result) {
    if (result.success && result.data) {
        // 1. Set basic Firebase user info (optional but good practice)
        const currentFirebaseUser = firebase.auth().currentUser
        if (currentFirebaseUser) {
            authStore.setUser(currentFirebaseUser)
        } else {
            console.warn(
                'OAuth successful but Firebase currentUser is null. Cannot set basic user info.'
            )
        }

        // 2. Set the detailed backend user data DIRECTLY
        authStore.user.data = result.data
        console.log(
            'Auth store updated directly with backend data:',
            authStore.user.data
        )

        // 3. Show appropriate notification
        toastUtils.showSuccess(
            toast,
            result.isNew
                ? t('login.user-registered')
                : t('login.login-successful'),
            null // Using a generic success detail
        )

        // 4. Navigate to the appropriate page (unless redirect is disabled)
        if (props.disableRedirect) {
            // Handle pending actions (e.g., signup bonus) before closing
            await handlePendingAction({ isAnonymousUpgrade: result.isNew })
            // Close the modal since we're staying on the same route
            modalStore.closeModal()
            return
        }

        let postLoginRedirected = false
        if (!result.isNew) {
            // Only check for post-login target if it was a login (existing user)
            postLoginRedirected = await handlePostLoginRedirect()
        }
        if (postLoginRedirected) return // Stop if redirected to specific chat by handlePostLoginRedirect

        const routeName = await getRedirectRoute(result.data, result.isNew)

        if (routeName) {
            // Prevent stale referral code for non-referral logins (existing users bypassing account-creation)
            if (!result.isNew) {
                localStorage.removeItem('loginTargetCreatorRefCode')
                await waitForAppInitialization()
            }
            router.push({ name: routeName })
        } else {
            // Handle error case if routeName is null (e.g., user type missing from result.data)
            console.error(
                'OAuth successful but could not determine redirect route from backend data:',
                result.data
            )
            toastUtils.showError(toast, t('general.general-error'))
        }
    } else if (result.userCancelled) {
        // User closed the popup - often no notification is needed
        console.log('Sign-in cancelled by user.')
    } else {
        // Handle other errors reported by signInWithProvider
        console.error('OAuth Error:', result.error, 'Code:', result.code)
        // Clear potential target if OAuth fails
        localStorage.removeItem('loginTargetCreatorId')
        localStorage.removeItem('loginTargetCreatorUsername')
        localStorage.removeItem('previewCreatorData')
        toastUtils.showError(
            toast,
            result.error || t('general.general-error') // Show specific error or fallback
        )
    }
}

const handleOAuthSignIn = async (providerFn) => {
    // Prevent multiple attempts
    if (loadingOAuth.value) {
        console.log('OAuth sign-in already in progress.')
        return
    }

    loadingOAuth.value = true

    try {
        const isAnonymous = firebase.auth().currentUser?.isAnonymous

        if (isAnonymous) {
            const provider = providerFn()
            const result = await firebase
                .auth()
                .currentUser.linkWithPopup(provider)

            // Update backend to remove isAnonymous flag
            await UserService.updateUser({
                isAnonymous: false,
                email: result.user.email,
            })

            // Fetch fresh user data
            await authStore.updateUserData(true)

            toastUtils.showSuccess(
                toast,
                t('general.welcome'),
                t('login.success')
            )

            // This flow upgrades an anonymous user (first registration).
            await handlePendingAction({ isAnonymousUpgrade: true })
        } else {
            const result = await authUtils.signInWithProvider(providerFn)
            // Process the result using the handler function
            await handleAuthResult(result)
        }
    } catch (error) {
        // Catch any unexpected errors
        console.error(
            'Unexpected error during OAuth sign-in initiation:',
            error
        )
        toastUtils.showError(toast, t('general.general-error'))
    } finally {
        // Reset loading state regardless of outcome
        loadingOAuth.value = false
    }
}

// Use the updated handleOAuthSignIn
const signInWithGoogle = () => handleOAuthSignIn(authUtils.googleProvider)
const signInWithX = () => handleOAuthSignIn(authUtils.xProvider)

async function waitForAppInitialization() {
    const maxWaitTime = 15000 // Increased to 15 seconds for cross-tab email verification flows
    const startTime = Date.now()

    while (
        !authStore.state.appInitialized &&
        Date.now() - startTime < maxWaitTime
    ) {
        await new Promise((resolve) => setTimeout(resolve, 100))
    }

    if (!authStore.state.appInitialized) {
        console.warn(
            'App initialization did not complete in time, proceeding anyway'
        )
        console.log('Current auth state:', {
            hasUserId: !!authStore.user?.data?._id,
            hasUid: !!authStore.user?.uid,
            appInitialized: authStore.state.appInitialized,
        })
        console.log(
            'Note: This is usually fine - the app will continue initializing in the background'
        )
    } else {
        console.log('App initialization completed, user data ready')
    }
}

async function handleVerificationSuccess() {
    try {
        const user = await UserService.getCurrentUser()

        if (user && user.status === 200 && user.data) {
            authStore.user.data = user.data

            const redirected = await handlePostLoginRedirect()
            if (redirected) {
                modalStore.closeModal()
                resetAuthForm()
                return
            }

            const routeName = await getRedirectRoute(
                user.data,
                !user.data.finishedSetup
            )

            if (routeName) {
                await waitForAppInitialization()
                modalStore.closeModal()
                router.push({ name: routeName })
            } else {
                toastUtils.showError(toast, t('general.general-error'))
            }
        } else {
            toastUtils.showError(toast, t('general.general-error'))
        }
    } catch (error) {
        console.error('Error handling verified redirect:', error)
        toastUtils.showError(toast, t('general.general-error'))
    } finally {
        showEmailVerification.value = false
        isLoadingEmailAuth.value = false
        resetAuthForm()
    }
}

// Function to focus the email input field
async function focusEmailInput() {
    await nextTick()
    if (emailInputRef.value && !showEmailVerification.value) {
        try {
            // PrimeVue InputText component - access the input element
            const component = emailInputRef.value
            let inputElement = null

            // PrimeVue InputText exposes the input via $el or we can query it
            if (component.$el) {
                // $el might be the input itself or a wrapper
                inputElement =
                    component.$el.tagName === 'INPUT'
                        ? component.$el
                        : component.$el.querySelector('input')
            }

            // Fallback: query by class or placeholder
            if (!inputElement) {
                inputElement =
                    document.querySelector('.input-field input') ||
                    document.querySelector(
                        'input[placeholder="email@example.com"]'
                    )
            }

            if (inputElement && typeof inputElement.focus === 'function') {
                // Small delay to ensure modal animation completes
                setTimeout(() => {
                    inputElement.focus()
                }, 100)
            }
        } catch (error) {
            console.warn('Could not focus email input:', error)
        }
    }
}

// Focus email input when component mounts (modal opens)
onMounted(() => {
    focusEmailInput()
})

// Focus email input when component is activated (if using keep-alive)
onActivated(() => {
    focusEmailInput()
})

// Watch for when email verification modal closes to refocus email input
watch(showEmailVerification, (newVal) => {
    if (!newVal) {
        // Email verification modal closed, refocus email input
        focusEmailInput()
    }
})
</script>

<style scoped>
.continue-outer-container {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
}

.verification-modal-container {
    width: 100%;
    max-width: 320px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px 0;
}

.main-auth-content {
    width: 250px;
    max-width: 250px;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 20px 0;
}

.welcome-title {
    font-size: 30px;
    font-weight: 100;
    margin-bottom: 20px;
    line-height: 1.3;
    text-align: center;
    font-family: 'Basier Circle', sans-serif;
    text-wrap: balance;
    color: #393939;
}

.continue-description {
    font-size: 14px;
    color: #393939;
    text-align: center;
    margin-bottom: 40px;
    line-height: 1.4;
    font-family: 'Nunito', sans-serif;
    width: 250px;
}

.shadow-btn {
    margin-bottom: 12px;
    width: 100%;
    height: 50px;
    border-radius: 15px;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 2px 5px #00000021;
    font-size: 14px;
    font-weight: 300;
    color: rgb(23, 23, 23);
}

.google-button {
    background-color: #fff;
    color: #000;
    border: none;
}

.x-button {
    background-color: #000;
    color: #fff;
    border: none;
}

.email-button {
    background-color: #f0f0f0;
    color: #333;
    border: none;
}

.button-icon {
    margin-right: 10px;
    width: 20px;
    height: 20px;
}

.link_terms {
    color: #393939;
    text-decoration: underline;
}

/* Divider styles */
.divider {
    display: flex;
    align-items: center;
    width: 100%;
    margin: 15px 0;
    gap: 15px;
}

.divider-line {
    flex: 1;
    height: 1px;
    background-color: #ddd;
}

.divider-text {
    color: #999;
    font-size: 14px;
    white-space: nowrap;
}

/* Email input styles */
.email-input-container {
    width: 100%;
    margin-bottom: 12px;
}

.input-wrapper {
    position: relative;
}

/* input {
  width: 256px !important;
  height: 24px !important;
} */

.input-field {
    width: 100%;
    height: 44px;
    border-radius: 8px !important;
    padding: 10px 12px !important;
    font-size: 14px !important;
    border: 1px solid #ddd !important;
    color: #333 !important;
}

input[data-pc-name='inputtext'] {
    border: 1px solid #ddd !important;
    color: #333 !important;
    z-index: 1;
    /* padding-right: 35px !important; */
}

input[data-pc-name='inputtext']::placeholder {
    color: #999 !important;
}

/* Forgot password link */
.forgot-password-link {
    width: 100%;
    text-align: right;
    margin-bottom: 15px;
}

.forgot-password-link a {
    cursor: pointer;
    color: black;
    font-size: 12px !important;
    text-decoration: underline;
    cursor: pointer;
}

/* Slide-fade transition animation for password field */
.slide-fade-enter-active {
    transition: all 0.3s ease-out;
}

.slide-fade-leave-active {
    transition: all 0.3s cubic-bezier(1, 0.5, 0.8, 1);
}

.slide-fade-enter-from,
.slide-fade-leave-to {
    transform: translateY(-20px);
    opacity: 0;
}

/* Email checking indicator styles */
.input-spinner {
    position: absolute;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    color: #6e3dea;
    z-index: 2;
}

.checking-email {
    border-color: #6e3dea !important;
}

/* Button text transition */
.button-text-fade-enter-active,
.button-text-fade-leave-active {
    transition:
        opacity 0.2s ease,
        transform 0.2s ease;
}

.button-text-fade-enter-from {
    opacity: 0;
    transform: translateY(10px);
}

.button-text-fade-leave-to {
    opacity: 0;
    transform: translateY(-10px);
}

.button-text {
    width: 218px;
}

/* Optional: Style for disabled buttons */
.shadow-btn:disabled {
    opacity: 0.7;
    cursor: not-allowed;
}

/* Dark mode loading overlay */
.dark-loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.8);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    z-index: 10000;
    gap: 16px;
}

.loading-spinner {
    width: 40px;
    height: 40px;
    border: 3px solid rgba(255, 255, 255, 0.2);
    border-top-color: white;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

.loading-text {
    color: white;
    font-size: 14px;
    font-family: 'Nunito', sans-serif;
    margin: 0;
}

/* Dark mode styles */
.continue-outer-container.dark-mode .welcome-title {
    color: white;
}

.continue-outer-container.dark-mode .continue-description {
    color: rgba(255, 255, 255, 0.75);
    font-weight: 300;
}

.continue-outer-container.dark-mode .link_terms {
    color: rgba(255, 255, 255, 0.75);
}

.continue-outer-container.dark-mode .shadow-btn {
    background-color: rgba(255, 255, 255, 0.1);
    color: white;
    box-shadow: none;
    border-radius: 52px;
}

.continue-outer-container.dark-mode .google-button,
.continue-outer-container.dark-mode .x-button,
.continue-outer-container.dark-mode .email-button {
    background-color: rgba(255, 255, 255, 0.1);
    color: white;
}

.continue-outer-container.dark-mode .email-button {
    filter: brightness(0) invert(1);
}

.continue-outer-container.dark-mode .divider-line {
    background-color: rgba(255, 255, 255, 0.2);
}

.continue-outer-container.dark-mode .divider-text {
    color: rgba(255, 255, 255, 0.6);
}

.continue-outer-container.dark-mode .input-field {
    background-color: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.2) !important;
    color: white !important;
}

.continue-outer-container.dark-mode .input-field::placeholder {
    color: rgba(255, 255, 255, 0.5) !important;
}

.continue-outer-container.dark-mode input[data-pc-name='inputtext'] {
    background-color: transparent;
    border: none;
    color: rgba(255, 255, 255, 0.75) !important;
}

.continue-outer-container.dark-mode
    input[data-pc-name='inputtext']::placeholder {
    color: rgba(255, 255, 255, 0.5) !important;
}

.continue-outer-container.dark-mode .forgot-password-link a {
    color: rgba(255, 255, 255, 0.75);
}
</style>

<style>
/* Set input field styles */
.p-inputtext {
    width: 100%;
    box-sizing: border-box;
}

/* Password field styles */
#password input {
    width: 100%;
    height: 44px;
    border-radius: 8px;
    padding: 10px 12px;
    font-size: 14px;
    background-color: #fff;
    border: 1px solid #ddd !important;
    color: #333 !important;
    z-index: 1;
}

#password input::placeholder {
    color: #999 !important;
}

#password input:focus {
    outline: 0 none;
    outline-offset: 0;
    box-shadow: none;
    border-color: #6e3dea !important;
}

/* Dark mode password field */
.continue-outer-container.dark-mode #password input {
    background-color: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.2) !important;
    color: white !important;
}

.continue-outer-container.dark-mode #password input::placeholder {
    color: rgba(255, 255, 255, 0.5) !important;
}

.continue-outer-container.dark-mode #password input:focus {
    border-color: rgba(255, 255, 255, 0.4) !important;
}
</style>
